import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from './AuthContext'
import { apiClient } from '../api/client'

export type Locale = 'en' | 'fr'

interface LocaleContextType {
  locale: Locale
  setLocale: (locale: Locale) => void
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined)

function isLocale(value: unknown): value is Locale {
  return value === 'en' || value === 'fr'
}

function getBrowserLocale(): Locale {
  return navigator.language?.startsWith('fr') ? 'fr' : 'en'
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, updateAuthUser, orgCode } = useAuth()
  const { i18n } = useTranslation()
  const queryClient = useQueryClient()

  const profileLanguage = user?.properties?.language
  const initialLocale = isLocale(profileLanguage) ? profileLanguage : getBrowserLocale()

  const [locale, setLocaleState] = useState<Locale>(initialLocale)
  const lastProfileLanguageRef = useRef(profileLanguage)

  // The API renders generated text (issue titles, rule labels) in the request's
  // language, and the language is not part of the query keys: refetch what is
  // cached when it changes, so nothing stays in the previous language.
  useEffect(() => {
    const onChange = () => { void queryClient.invalidateQueries() }
    i18n.on('languageChanged', onChange)
    return () => { i18n.off('languageChanged', onChange) }
  }, [i18n, queryClient])

  // Sync locale when user profile language changes externally (login, profile update)
  useEffect(() => {
    if (isAuthenticated && isLocale(profileLanguage) && profileLanguage !== lastProfileLanguageRef.current) {
      lastProfileLanguageRef.current = profileLanguage
      setLocaleState(profileLanguage)
      i18n.changeLanguage(profileLanguage)
    }
  }, [isAuthenticated, profileLanguage, i18n])

  const setLocale = (newLocale: Locale) => {
    lastProfileLanguageRef.current = newLocale

    // Persist language to user profile if authenticated
    if (isAuthenticated && user && orgCode && user.uid) {
      updateAuthUser({
        properties: { ...user.properties, language: newLocale }
      })

      // Own profile, so /users/me (spec 2026-10-23-05): a member without
      // `contacts.write` can still save their language. The server merges
      // the key into the stored properties.
      apiClient.PATCH('/api/v1/organizations/{orgCode}/users/me', {
        params: { path: { orgCode } },
        body: { properties: { language: newLocale } }
      }).then(({ error }) => {
        if (error) console.error('Failed to persist language preference:', error)
        // The own profile page reads the same record: drop its cached copy.
        else queryClient.invalidateQueries({ queryKey: ['get', '/api/v1/organizations/{orgCode}/users/me'] })
      }).catch(err => console.error('Failed to persist language preference:', err))
    }

    // After the save request is issued: changing the language refetches every
    // cached query, and that burst must not queue ahead of the save (a page
    // change right after the toggle then dropped it).
    setLocaleState(newLocale)
    i18n.changeLanguage(newLocale)
  }

  // Sync i18next on mount
  useEffect(() => {
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      {children}
    </LocaleContext.Provider>
  )
}

export function useLocale() {
  const context = useContext(LocaleContext)
  if (!context) throw new Error('useLocale must be used within a LocaleProvider')
  return context
}
